<template>
  <div>
    <andes-header></andes-header>

    <div class="container refund-page">
      <div class="row">
        <div class="col-sm-9">

          <div class="row">
            <div class="col-12">
              <div class="current-srch-container">
                <div class="row d-flex align-items-center text-center text-sm-left">
                  <div class="col-12 col-sm-6">
                    <h4 class="m-0">{{ $t('SOLICITAR DEVOLUCIÓN') }}</h4>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="reservation text-center">{{ $t('Reserva:') }} {{ booking.RecordLocator }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr>

          <div class="alert alert-danger" v-if="error">{{ $t(error) }}</div>
          <div class="alert alert-warning" v-if="options && !options.Enabled">
            {{ $t(options.Message || 'La devolución online no está disponible.') }}
          </div>
          <div class="alert alert-warning" v-else-if="options && !options.Allowed && !selectedFlight">
            {{ $t(options.Message || 'Esta reserva no tiene vuelos disponibles para devolución online.') }}
          </div>

          <!-- PASO 1: ELEGIR VUELO -->
          <div v-if="step === 1 && options && options.Enabled">
            <h5 class="mb-3">{{ $t('¿Qué vuelo querés devolver?') }}</h5>
            <div class="card shadow mb-3" v-for="flight of options.Flights" :key="flight.FlightKey">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <div class="flight-route">
                      {{ flight.DepartureStation }} → {{ flight.ArrivalStation }}
                      <span class="flight-number" v-if="flight.FlightNumber">· {{ flight.FlightNumber }}</span>
                    </div>
                    <div class="text-muted">
                      {{ formatDateTime(flight.STD) }}
                      <span v-if="flight.ProductName">· {{ flight.ProductName }}</span>
                    </div>
                    <div class="text-muted" v-if="flight.Allowed">
                      {{ $t('Monto a recibir:') }}
                      <strong>{{ flight.Currency }} {{ toCurrencyFormat(flight.RefundAmount) }}</strong>
                      <span v-if="flight.PassengerCount > 1">
                        ({{ flight.PassengerCount }} {{ $t('pasajeros') }})
                      </span>
                    </div>
                    <div class="text-muted" v-if="flight.Allowed && !flight.FareRefundable">
                      {{ $t('La tarifa no permite devolución: solo se reintegran las tasas.') }}
                    </div>
                    <div class="text-danger" v-if="!flight.Allowed">{{ $t(flight.Message) }}</div>
                  </div>
                  <button type="button" class="btn btn-andes mt-2" v-if="flight.Allowed"
                          :disabled="loading" @click="pickFlight(flight)">
                    {{ $t('Solicitar') }}
                  </button>
                </div>
              </div>
            </div>
            <button type="button" class="btn btn-andes" @click="backToItinerary">{{ $t('Volver al itinerario') }}</button>
          </div>

          <!-- PASO 2: CONFIRMAR MONTO -->
          <div v-if="step === 2 && selectedFlight">
            <h5 class="mb-3">{{ $t('Confirmá la devolución') }}</h5>
            <div class="card shadow mb-3">
              <div class="card-body">
                <div class="flight-route mb-2">
                  {{ selectedFlight.DepartureStation }} → {{ selectedFlight.ArrivalStation }}
                  <span class="flight-number" v-if="selectedFlight.FlightNumber">
                    · {{ selectedFlight.FlightNumber }}
                  </span>
                </div>
                <div class="text-muted mb-3">{{ formatDateTime(selectedFlight.STD) }}</div>

                <div class="alert alert-info" v-if="!selectedFlight.FareRefundable">
                  La tarifa no permite devolución. Solo se devolverán las tasas reintegrables.
                </div>

                <table class="table segment-detail mb-3">
                  <tr>
                    <th>{{ $t('Monto a recibir') }}</th>
                    <td>
                      <strong>
                        {{ selectedFlight.Currency }}
                        {{ toCurrencyFormat(selectedFlight.RefundAmount) }}
                      </strong>
                    </td>
                  </tr>
                  <tr v-if="!selectedFlight.FareRefundable">
                    <th>{{ $t('Cargo de cancelación') }}</th>
                    <td>
                      {{ selectedFlight.Currency }}
                      {{ toCurrencyFormat(selectedFlight.CancellationFeeAmount) }}
                    </td>
                  </tr>
                  <tr>
                    <th>{{ $t('Pasajeros') }}</th>
                    <td>{{ selectedFlight.PassengerCount }}</td>
                  </tr>
                </table>

                <p class="mb-3">
                  Al confirmar se procesará la devolución del pago. El vuelo se cancela
                  únicamente si la devolución se acredita correctamente.
                </p>

                <div class="d-flex flex-wrap" style="gap: 10px;">
                  <button type="button" class="btn btn-andes"
                          :disabled="loading" @click="confirmRefund">
                    Confirmar devolución de
                    {{ selectedFlight.Currency }}
                    {{ toCurrencyFormat(selectedFlight.RefundAmount) }}
                  </button>
                  <button type="button" class="btn btn-outline-secondary"
                          :disabled="loading" @click="step = 1; selectedFlight = null">
                    {{ $t('Volver') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <andes-footer/>
    <loading-modal v-if="loading"></loading-modal>

    <transition name="fade">
      <div class="modal-mask" v-if="successMessage">
        <div class="modal-wrapper">
          <div class="modal-container refund-result-modal">
            <div class="modal-body">
              <div class="card">
                <div class="card-header">
                  <h4>{{ $t('Devolución') }}</h4>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ successMessage }}</p>
                </div>
                <div class="card-footer text-right">
                  <button type="button" class="btn btn-andes" @click="closeSuccess">{{ $t('Aceptar') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import AndesHeader from '@/components/AndesHeader';
import AndesFooter from '@/components/AndesFooter';
import LoadingModal from '@/components/LoadingModal';
import { getRefundOptions, confirmBookingRefund } from '@/api/api';
import '@/css/modal.css';

export default {
  name: 'refund-page',
  components: { AndesHeader, AndesFooter, LoadingModal },
  data: function () {
    return {
      options: null,
      selectedFlight: null,
      step: 1,
      error: null,
      successMessage: null,
    };
  },
  computed: {
    ...mapState(['booking', 'loading']),
  },
  methods: {
    toCurrencyFormat: function (value) {
      let n = parseFloat(value || 0);
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDateTime: function (value) {
      if (!value) return '';
      return moment(value).locale('es').format('ddd DD MMM YYYY HH:mm');
    },
    onError: function (error) {
      this.$store.commit('loading', false);
      this.error = typeof error === 'string' ? error : (error && error.message) || 'Error';
    },
    load: function () {
      if (!this.booking || !this.booking.BookingID) {
        this.$router.replace('/');
        return;
      }
      this.error = null;
      this.$store.commit('loading', true);
      getRefundOptions(this.booking.BookingID, (result) => {
        this.$store.commit('loading', false);
        this.options = result;
      }, this.onError);
    },
    pickFlight: function (flight) {
      this.selectedFlight = flight;
      this.step = 2;
      this.error = null;
    },
    confirmRefund: function () {
      if (!this.selectedFlight) return;
      this.error = null;
      this.$store.commit('loading', true);
      confirmBookingRefund({
        BookingID: this.booking.BookingID,
        SegmentID: this.selectedFlight.SegmentID,
      }, (result) => {
        this.$store.commit('loading', false);
        let message = (result && result._RefundResult && result._RefundResult.Message)
          || this.$t('Devolución procesada correctamente');
        if (result) {
          delete result._RefundResult;
          this.$store.dispatch('updateBooking', result);
        }
        this.successMessage = message;
      }, this.onError);
    },
    closeSuccess: function () {
      this.successMessage = null;
      this.$router.replace('itinerary');
    },
    backToItinerary: function () {
      this.$router.replace('itinerary');
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.refund-page .flight-route {
  font-size: 1.15rem;
  font-weight: 600;
}
.refund-page .flight-number {
  font-weight: 400;
  color: #666;
}
.refund-page .segment-detail th {
  width: 40%;
}
.refund-result-modal {
  width: 420px;
  max-width: 92vw;
  padding: 0;
  background: transparent;
  box-shadow: none;
}
.refund-result-modal .card-header h4 {
  margin: 0;
}
</style>
